import { Injectable } from '@angular/core';
import { createInstance } from '@module-federation/runtime';
import { environment } from '@env/insitestore360';

export type MicroFrontendRemoteId = 'spft' | 'shopProfiling';

export type MicroFrontendMountFn = (
  container: HTMLElement,
  options?: {
    readonly initialPath?: string;
    readonly apiBase?: string;
    readonly shellBasePath?: string;
    readonly onShellNavigate?: (shellUrl: string) => void;
    readonly setNavigator?: (navigate: ((to: string) => void) | null) => void;
  },
) => (() => void) | void;

type MountModule = {
  mount?: MicroFrontendMountFn;
  default?: {
    mount?: MicroFrontendMountFn;
    unmount?: () => void;
  };
};

type EnvironmentRemotes = {
  spftRemoteEntry?: string;
  shopProfilingRemoteEntry?: string;
  spftUiUrl?: string;
  shopProfilingUiUrl?: string;
};

type FederationRuntime = {
  registerRemotes: (
    remotes: Array<{ name: string; entry: string; type?: string }>,
  ) => void;
  loadRemote: (id: string) => Promise<unknown>;
};

const DEFAULT_REMOTE_ENTRIES: Record<MicroFrontendRemoteId, string> = {
  spft: '/public/spft/mf-manifest.json',
  shopProfiling: '/public/shop-profiling/mf-manifest.json',
};

/**
 * Loads React remotes via Module Federation runtime into the Angular shell.
 * Remotes run in the same window → shared localStorage / SSO session.
 */
@Injectable({
  providedIn: 'root',
})
export class MicroFrontendLoaderService {
  private readonly mf: FederationRuntime = createInstance({
    name: 'insitestore360',
    remotes: [],
  }) as FederationRuntime;

  private remotesRegistered = false;
  private readonly mountCache = new Map<MicroFrontendRemoteId, MicroFrontendMountFn>();

  public async loadMount(remoteId: MicroFrontendRemoteId): Promise<MicroFrontendMountFn> {
    const cached = this.mountCache.get(remoteId);
    if (cached) {
      return cached;
    }

    this.ensureRemotesRegistered();

    const exposed = remoteId === 'spft' ? 'spft/Mount' : 'shopProfiling/Mount';
    const mod = (await this.mf.loadRemote(exposed)) as MountModule | null;
    const mountFn = mod?.mount ?? mod?.default?.mount;

    if (typeof mountFn !== 'function') {
      throw new Error(`Micro-frontend remote "${remoteId}" did not expose mount()`);
    }

    this.mountCache.set(remoteId, mountFn);
    return mountFn;
  }

  public resolveRemoteEntry(remoteId: MicroFrontendRemoteId): string {
    const env = environment as EnvironmentRemotes;
    if (remoteId === 'spft') {
      return (
        env.spftRemoteEntry ||
        this.manifestFromUiUrl(env.spftUiUrl, DEFAULT_REMOTE_ENTRIES.spft) ||
        DEFAULT_REMOTE_ENTRIES.spft
      );
    }
    return (
      env.shopProfilingRemoteEntry ||
      this.manifestFromUiUrl(env.shopProfilingUiUrl, DEFAULT_REMOTE_ENTRIES.shopProfiling) ||
      DEFAULT_REMOTE_ENTRIES.shopProfiling
    );
  }

  private ensureRemotesRegistered(): void {
    if (this.remotesRegistered) {
      return;
    }

    this.mf.registerRemotes([
      {
        name: 'spft',
        entry: this.resolveRemoteEntry('spft'),
        type: 'module',
      },
      {
        name: 'shopProfiling',
        entry: this.resolveRemoteEntry('shopProfiling'),
        type: 'module',
      },
    ]);
    this.remotesRegistered = true;
  }

  private manifestFromUiUrl(
    uiUrl: string | undefined,
    fallback: string,
  ): string {
    if (!uiUrl) {
      return fallback;
    }
    if (uiUrl.includes('mf-manifest.json') || uiUrl.includes('remoteEntry.js')) {
      return uiUrl;
    }
    try {
      const base = uiUrl.replace(/\/index\.html.*$/, '/');
      return new URL('mf-manifest.json', window.location.origin + base).pathname;
    } catch {
      return fallback;
    }
  }
}
